import React, { useRef, useState } from "react";
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    useSortable,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { ImagePlus, Loader2, X, GripVertical } from "lucide-react";
import { uploadFile, publicAssetUrl } from "@/lib/api";

const MAX_IMAGES = 8;
const MAX_MB = 5;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

/**
 * Reusable multi-image gallery editor for offer posts.
 *
 * Props:
 *   - value: string[]  Ordered list of upload URLs currently in the gallery.
 *   - onChange: (urls: string[]) => void
 *   - testidPrefix: string used to build data-testid attrs (default "offer-gallery")
 *   - disabled: boolean
 */
export default function OfferGallery({
    value = [],
    onChange,
    testidPrefix = "offer-gallery",
    disabled = false,
}) {
    const urls = Array.isArray(value) ? value : [];
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const validateFile = (file) => {
        if (!ALLOWED_TYPES.includes(file.type)) {
            toast.error(`${file.name}: only JPG, PNG, or WEBP allowed`);
            return false;
        }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`${file.name}: max ${MAX_MB} MB per image`);
            return false;
        }
        return true;
    };

    const handleFiles = async (fileList) => {
        if (disabled) return;
        const files = Array.from(fileList || []);
        if (!files.length) return;

        const slots = MAX_IMAGES - urls.length;
        if (slots <= 0) {
            toast.error(`Max ${MAX_IMAGES} gallery images`);
            return;
        }
        if (files.length > slots) {
            toast.error(`Only ${slots} slot(s) left — added first ${slots}`);
        }
        const accepted = [];
        for (const f of files.slice(0, slots)) {
            if (validateFile(f)) accepted.push(f);
        }
        if (!accepted.length) return;

        try {
            setUploading(true);
            const results = await Promise.all(
                accepted.map((f) => uploadFile(f, "gallery")),
            );
            const newUrls = results.map((r) => r.url).filter(Boolean);
            onChange?.([...urls, ...newUrls]);
            toast.success(
                newUrls.length === 1
                    ? "Image added to gallery"
                    : `${newUrls.length} images added to gallery`,
            );
        } catch (e) {
            toast.error("Upload failed — please retry");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const removeAt = (idx) => {
        if (disabled) return;
        const next = urls.filter((_, i) => i !== idx);
        onChange?.(next);
    };

    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const from = urls.findIndex((u) => u === active.id);
        const to = urls.findIndex((u) => u === over.id);
        if (from < 0 || to < 0) return;
        onChange?.(arrayMove(urls, from, to));
    };

    const canAddMore = urls.length < MAX_IMAGES && !disabled;

    return (
        <section
            className="rounded-2xl border border-neutral-200 bg-white p-4"
            data-testid={`${testidPrefix}-section`}
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-bold text-neutral-800">
                        Gallery images
                    </div>
                    <div className="mt-0.5 text-xs text-neutral-500">
                        Up to {MAX_IMAGES} images · {MAX_MB} MB each · JPG / PNG / WEBP.
                        Drag to reorder. The hero image stays separate.
                    </div>
                </div>
                <span
                    className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600"
                    data-testid={`${testidPrefix}-count`}
                >
                    {urls.length}/{MAX_IMAGES}
                </span>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
            >
                <SortableContext items={urls} strategy={rectSortingStrategy}>
                    <div
                        className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4"
                        data-testid={`${testidPrefix}-grid`}
                    >
                        {urls.map((u, i) => (
                            <GalleryThumb
                                key={u}
                                id={u}
                                url={u}
                                index={i}
                                onRemove={() => removeAt(i)}
                                testidPrefix={testidPrefix}
                                disabled={disabled}
                            />
                        ))}
                        {canAddMore && (
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                disabled={uploading}
                                className="group flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-neutral-300 bg-neutral-50 text-neutral-500 transition hover:border-orange-400 hover:bg-orange-50 hover:text-orange-600 disabled:opacity-50"
                                data-testid={`${testidPrefix}-add-btn`}
                            >
                                {uploading ? (
                                    <Loader2 className="animate-spin" size={20} />
                                ) : (
                                    <ImagePlus size={22} />
                                )}
                                <span className="text-[11px] font-semibold">
                                    {uploading ? "Uploading" : "Add image"}
                                </span>
                            </button>
                        )}
                    </div>
                </SortableContext>
            </DndContext>

            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid={`${testidPrefix}-file-input`}
            />
        </section>
    );
}

function GalleryThumb({ id, url, index, onRemove, testidPrefix, disabled }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="group relative aspect-square overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100"
            data-testid={`${testidPrefix}-thumb-${index}`}
        >
            <img
                src={publicAssetUrl(url)}
                alt={`Gallery item ${index + 1}`}
                className="h-full w-full object-cover"
                draggable={false}
            />
            <span
                className="absolute left-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-black/70 px-1.5 text-[11px] font-bold text-white"
                data-testid={`${testidPrefix}-index-${index}`}
            >
                {index + 1}
            </span>
            {!disabled && (
                <>
                    <button
                        type="button"
                        onClick={onRemove}
                        aria-label={`Remove image ${index + 1}`}
                        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white opacity-90 transition hover:bg-rose-600"
                        data-testid={`${testidPrefix}-remove-${index}`}
                    >
                        <X size={13} />
                    </button>
                    <button
                        type="button"
                        aria-label={`Drag image ${index + 1}`}
                        className="absolute bottom-1.5 right-1.5 flex h-6 w-6 cursor-grab items-center justify-center rounded-full bg-black/70 text-white opacity-90 active:cursor-grabbing"
                        data-testid={`${testidPrefix}-drag-${index}`}
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical size={13} />
                    </button>
                </>
            )}
        </div>
    );
}
